import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { App, Popover } from 'antd';
import { useTranslate } from '@/translations';
import {
  CustomColumns,
  CustomTable,
  VehicleInformation,
  faEllipsisVertical,
  Icon,
  LicensePlate,
} from '@/ui/components';
import { StorageVehicle, AppSearch } from '@/types';
import {
  fetchStorageVehiclesForExport,
  useCheckoutVehicles,
  useStorageVehiclesFilters,
  useStorageVehiclesList,
} from '@/api/Storage';
import { FORMAT_FULL_TIME_DAYJS, StorageKeys } from '@/utils/constants';
import { timeConverter } from '@/utils/helpers';

const defaultPageSize = 10;

export const StorageVehicleGridDetail: FC = () => {
  const { t } = useTranslate();
  const { message, modal } = App.useApp();

  const { data: filterOptions, isFetching: filtersLoading } =
    useStorageVehiclesFilters();

  const defaultAppSearch: AppSearch<StorageVehicle> = useMemo(
    () => ({
      name: undefined,
      search: undefined,
      page: 1,
      pageSize: defaultPageSize,
      sortField: undefined,
      sortOrder: undefined,
      filters: [],
    }),
    [],
  );

  const [query, setQuery] = useState<AppSearch<StorageVehicle> | undefined>(
    undefined,
  );

  const { mutateAsync: checkoutVehiclesAsync } = useCheckoutVehicles();

  const { data: searchResponse, isFetching: vehiclesLoading } =
    useStorageVehiclesList(query);

  const data = searchResponse?.entities ?? [];
  const total = searchResponse?.totalCount ?? 0;

  useEffect(() => {
    if (searchResponse === undefined || query === undefined) return;
    const entities = searchResponse.entities ?? [];
    if ((query.page || 1) > 1 && entities.length === 0) {
      setQuery((prev) =>
        prev ? { ...prev, page: 1, pageSize: defaultPageSize } : prev,
      );
    }
  }, [searchResponse, query, query?.page]);

  const getExportData = async () => {
    if (!query) return [];
    return fetchStorageVehiclesForExport(query);
  };

  const onCheckout = useCallback(
    (value: string) => {
      modal.confirm({
        title: 'CONFIRMATION REQUIRED',
        content: `Are you sure you want to checkout vehicle ${value}?`,
        okText: t('yes'),
        onOk: async () => {
          const res = await checkoutVehiclesAsync(value);
          const success = res?.success || false;
          message.open({
            type: success ? 'success' : 'error',
            content: success
              ? 'Checkout vehicle successfully!'
              : 'Failed to checkout vehicle',
          });
        },
        onCancel: () => {},
      });
    },
    [checkoutVehiclesAsync, message, modal, t],
  );

  const columns: CustomColumns<StorageVehicle> = useMemo(
    () => [
      {
        dataIndex: 'vin',
        title: t('vin'),
        width: 180,
        sortable: true,
        filter: true,
        render: (value) => <VehicleInformation vin={value} showAsLabel />,
        exportRender: (value) => value || '',
        fixed: 'left',
      },
      {
        dataIndex: 'regNumber',
        title: t('regNumber'),
        width: 150,
        render: (value) =>
          value ? <LicensePlate regNumber={value} /> : t('noRegNumber'),
        exportRender: (value) => value || '',
        sortable: true,
        filter: true,
      },
      {
        dataIndex: 'fleetCode',
        title: t('fleetCode'),
        width: 120,
        sortable: true,
        filter: true,
        renderType: 'select',
        filterOptions: filterOptions?.fleetCodes || [],
      },
      {
        dataIndex: 'subCustomerName',
        title: t('subCustomer'),
        width: 150,
        sortable: true,
        filter: true,
      },
      {
        dataIndex: 'model',
        title: t('model'),
        width: 150,
        sortable: true,
        filter: true,
      },
      {
        dataIndex: 'manufacturer',
        title: t('manufacturer'),
        width: 150,
        sortable: true,
        filter: true,
        renderType: 'select',
        filterOptions: filterOptions?.manufacturers || [],
      },
      {
        dataIndex: 'firstArrival',
        title: t('firstArrivalDate'),
        width: 180,
        renderType: 'date',
        render: (value) => timeConverter(value, FORMAT_FULL_TIME_DAYJS),
        sortable: true,
        filter: true,
      },
      {
        dataIndex: 'siteArrival',
        title: t('siteArrivalDate'),
        width: 180,
        renderType: 'date',
        render: (value) => timeConverter(value, FORMAT_FULL_TIME_DAYJS),
        sortable: true,
        filter: true,
      },
      {
        dataIndex: 'arrivalLocation',
        title: t('currentSite'),
        width: 150,
        sortable: true,
        filter: true,
        renderType: 'select',
        filterOptions: filterOptions?.arrivalLocations || [],
      },
      {
        dataIndex: 'statusCode',
        title: t('status'),
        width: 150,
        sortable: true,
        filter: true,
        renderType: 'select',
        filterOptions: filterOptions?.statusCodes || [],
      },
      {
        dataIndex: 'machanicalInspectionDateTime',
        title: t('lastMechanicalInspection'),
        width: 180,
        renderType: 'date',
        render: (value) => timeConverter(value, FORMAT_FULL_TIME_DAYJS),
        sortable: true,
        filter: true,
      },
      {
        dataIndex: 'inspectionDateTime',
        title: t('lastInspection'),
        width: 180,
        renderType: 'date',
        render: (value) => timeConverter(value, FORMAT_FULL_TIME_DAYJS),
        sortable: true,
        filter: true,
      },
      {
        dataIndex: 'storageFullDesc',
        title: t('location'),
        width: 180,
        sortable: true,
        filter: true,
      },
      {
        dataIndex: 'vin',
        title: 'Actions',
        width: 80,
        align: 'center',
        fixed: 'right',
        exportable: false,
        render: (value, record) =>
          record.customerCode === 'Arv' ? (
            <Popover
              content={
                <span
                  className="cursor-pointer"
                  onClick={() => onCheckout(value)}
                >
                  Checkout Vehicle
                </span>
              }
              trigger="click"
              placement="left"
              arrow={false}
            >
              <div className="cursor-pointer">
                <Icon icon={faEllipsisVertical} />
              </div>
            </Popover>
          ) : null,
      },
    ],
    [filterOptions, onCheckout, t],
  );

  const onAppSearchChange = useCallback(
    (search: AppSearch<StorageVehicle>) => setQuery(search),
    [],
  );

  const appSearchOptions = useMemo(
    () => ({
      storageKey: StorageKeys.SearchStorageVehicles,
      defaultSearch: defaultAppSearch,
      value: query,
      onChange: onAppSearchChange,
    }),
    [defaultAppSearch, onAppSearchChange, query],
  );

  return (
    <CustomTable
      dataSource={data}
      columns={columns}
      rowKey="vin"
      loading={vehiclesLoading || filtersLoading}
      pagination={{
        total,
        pageSize: query?.pageSize || defaultPageSize,
        current: query?.page || 1,
      }}
      exportOptions={{ fileName: 'Storage Vehicles', getExportData }}
      appSearchOptions={appSearchOptions}
      scroll={{ x: 2400 }}
    />
  );
};
